import React, { useEffect, useState, useRef } from 'react';
import { Leaf, Check, ArrowRight, BookOpen, Gift, Lock, Heart, Trophy, Star, AlertTriangle, ShieldCheck, Droplets, ChevronDown } from 'lucide-react';
import { PageType } from '../App';
interface SalesPageProps {
  navigate: (page: PageType) => void;
}
// CSS-only FadeIn with IntersectionObserver
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(el);
      }
    }, {
      rootMargin: '-50px'
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} className={className} style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
    transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
  }} data-id="element-4">
      {children}
    </div>;
};
export function SalesPage({
  navigate
}: SalesPageProps) {
  const [selectedOffer, setSelectedOffer] = useState<'base' | 'pro'>('pro');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const toggleFaq = (index: number) => {
    if (openFaq === index) {
      setOpenFaq(null);
    } else {
      setOpenFaq(index);
    }
  };
  return <div className="min-h-screen bg-navy-900 font-sans text-offwhite overflow-x-hidden" data-id="element-5">
      {/* HERO SECTION */}
      <section className="relative pt-4 pb-6 px-4 md:pt-8 md:pb-8 border-b border-gold-500/20 flex flex-col justify-center" data-id="element-6">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-900/20 via-navy-900 to-navy-900 -z-10" data-id="element-7"></div>
        <div className="max-w-4xl mx-auto text-center" data-id="element-8">
          <FadeIn data-id="element-9">
            <div className="inline-block bg-red-500/10 border border-red-500/20 rounded-full px-4 py-1.5 mb-3" data-id="element-10">
              <span className="text-red-400 font-medium text-sm tracking-wide uppercase flex items-center gap-2" data-id="element-11">
                <AlertTriangle className="w-4 h-4" data-id="element-12" />
                Atención Familias
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-serif text-white leading-tight mb-3 drop-shadow-lg" data-id="element-13">
              Los Médicos Tratan la Infección Cuando Ya Está Instalada.
            </h1>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-serif text-gold-500 leading-tight mb-5 drop-shadow-lg" data-id="element-14">
              Este Sistema Te Enseña a Frenarla Antes de Que Pase a Mayores.
            </h2>

            <div className="flex justify-center mb-5" data-id="element-15">
              <img src="https://cdn.magicpatterns.com/uploads/rN2CiMksSwRM2LVZfGn2Pe/heylloyd-image-1773372626707.png" alt="El Botiquín Antibiótico Natural — 4 Tomos Completos" className="w-full max-w-sm md:max-w-md rounded-2xl shadow-2xl shadow-emerald-900/30" data-id="element-16" />
            </div>

            <p className="text-xl md:text-2xl text-gray-300 font-light max-w-3xl mx-auto leading-relaxed mb-3" data-id="element-17">
              Descubrí los 7 Antibióticos Naturales que Nuestros Abuelos Usaban
              Para Curar Infecciones, Gripes y Virus... Sin Recurrir a la
              Farmacia
            </p>

            <p className="text-lg text-gray-400 italic max-w-2xl mx-auto" data-id="element-18">
              El mismo conocimiento que sobrevivió 5.000 años. Ahora organizado
              en un protocolo de 7 días con dosis exactas, tablas por edad y el
              sistema completo para armar tu botiquín natural antes de que lo
              necesites.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* LEAD / HOOK SECTION */}
      <section className="py-8 px-4 bg-navy-900" data-id="element-19">
        <div className="max-w-3xl mx-auto" data-id="element-20">
          <div className="space-y-4 text-lg md:text-xl text-gray-300 leading-relaxed font-light" data-id="element-21">
            <FadeIn delay={0.1} data-id="element-22">
              <p data-id="element-23">Son las 2 de la mañana.</p>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-24">
              <p data-id="element-25">
                Tu hijo tiene 38.8 de fiebre. La garganta inflamada. Los
                ganglios duros.
              </p>
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-26">
              <p data-id="element-27">
                Abrís el botiquín y encontrás... el jarabe vencido. El
                antibiótico del mes pasado terminado. La farmacia de guardia a
                20 minutos y afuera llueve.
              </p>
            </FadeIn>
            <FadeIn delay={0.4} data-id="element-28">
              <p data-id="element-29">Y la pregunta que te paraliza no es qué darle.</p>
            </FadeIn>
            <FadeIn delay={0.5} data-id="element-30">
              <p className="text-gold-400 italic font-serif text-2xl my-4 text-center" data-id="element-31">
                ¿Esto lo manejo en casa... o tenemos que salir ahora mismo?
              </p>
            </FadeIn>
            <FadeIn delay={0.6} data-id="element-32">
              <p data-id="element-33">
                Si alguna vez viviste ese momento, sabés exactamente de qué
                estoy hablando.
              </p>
              <p className="mt-3" data-id="element-34">
                Esa mezcla de miedo, incertidumbre y sensación de no tener el
                control de algo tan básico como la salud de tu familia.
              </p>
            </FadeIn>
            <FadeIn delay={0.7} className="pt-2" data-id="element-35">
              <p data-id="element-36">Y lo peor no es el momento en sí.</p>
              <p className="text-white font-bold text-2xl mt-3" data-id="element-37">
                Lo peor es que ese momento se repite.
              </p>
              <p className="mt-3" data-id="element-38">
                Cada invierno. Cada cambio de estación. Cada vez que alguien en
                la familia trae algo del colegio o del trabajo.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* PROBLEM AGITATION */}
      <section className="py-8 px-4 bg-navy-800 border-y border-gold-500/10" data-id="element-39">
        <div className="max-w-3xl mx-auto" data-id="element-40">
          <FadeIn data-id="element-41">
            <h2 className="text-3xl md:text-4xl font-bold text-gold-500 font-serif mb-5 text-center" data-id="element-42">
              EL COSTO REAL
            </h2>
          </FadeIn>

          <div className="space-y-5 text-lg text-gray-300" data-id="element-43">
            <FadeIn data-id="element-44">
              <p data-id="element-45">
                ¿Sabés cuánto cuesta una consulta pediátrica de urgencia en
                Argentina hoy?
              </p>
              <p className="text-xl text-white font-bold mt-2" data-id="element-46">
                Entre $15.000 y $25.000 pesos. Por una sola consulta.
              </p>
            </FadeIn>

            <FadeIn data-id="element-47">
              <p className="mt-4" data-id="element-48">
                ¿Y el antibiótico que te recetan "por las dudas"?
              </p>
              <p className="text-xl text-white font-bold mt-2" data-id="element-49">
                Otros $8.000 a $15.000 pesos. Para una infección que en el 80%
                de los casos es viral y el antibiótico no hace absolutamente
                nada.
              </p>
            </FadeIn>

            <FadeIn data-id="element-50">
              <div className="bg-navy-900 p-6 rounded-2xl border-l-4 border-red-500 mt-8" data-id="element-51">
                <p className="mb-3 font-bold text-white" data-id="element-52">
                  Pero eso no es lo más grave.
                </p>
                <p className="mb-3" data-id="element-53">
                  Lo más grave es lo que nadie te dice en esa consulta de 10
                  minutos:
                </p>
                <ul className="space-y-2 pl-4" data-id="element-54">
                  <li className="flex items-start" data-id="element-55">
                    <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" data-id="element-56" />
                    Que cada antibiótico innecesario que toma tu familia
                    destruye la flora intestinal.
                  </li>
                  <li className="flex items-start" data-id="element-57">
                    <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" data-id="element-58" />
                    Que debilita el sistema inmunológico en lugar de
                    fortalecerlo.
                  </li>
                  <li className="flex items-start" data-id="element-59">
                    <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" data-id="element-60" />
                    <span data-id="element-61">
                      Que crea resistencia bacteriana, de modo que la próxima
                      vez que realmente lo necesiten...{' '}
                      <strong className="text-white" data-id="element-62">
                        ya no va a funcionar igual.
                      </strong>
                    </span>
                  </li>
                </ul>
              </div>
            </FadeIn>

            <FadeIn data-id="element-63">
              <div className="text-center space-y-3 mt-8 bg-navy-800/50 p-6 rounded-2xl border border-navy-700" data-id="element-64">
                <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" data-id="element-65" />
                <p className="text-gray-300" data-id="element-66">
                  La Organización Mundial de la Salud declaró en 2023 que la
                  resistencia bacteriana es una de las{' '}
                  <strong className="text-white" data-id="element-67">
                    10 amenazas más graves para la salud global.
                  </strong>
                </p>
                <p className="text-gold-400 font-medium" data-id="element-68">
                  Y Argentina consume 3 veces más antibióticos per cápita que la
                  media europea.
                </p>
                <p className="text-xl text-white font-bold mt-4" data-id="element-69">
                  Algo está muy mal en la forma en que manejamos las infecciones
                  en el hogar.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* THE TWIST */}
      <section className="py-8 px-4 bg-navy-900" data-id="element-70">
        <div className="max-w-3xl mx-auto" data-id="element-71">
          <FadeIn data-id="element-72">
            <h2 className="text-3xl md:text-4xl font-bold text-white font-serif mb-5 text-center" data-id="element-73">
              POR QUÉ LOS REMEDIOS SUELTOS NO FUNCIONAN
            </h2>
          </FadeIn>

          <div className="space-y-5 text-lg text-gray-300" data-id="element-74">
            <FadeIn data-id="element-75">
              <p data-id="element-76">Quizás ya intentaste los remedios caseros sueltos.</p>
              <p data-id="element-77">
                Un té de jengibre por acá. Un poco de miel por allá. Propóleo
                que alguien te recomendó.
              </p>
              <p className="mt-3" data-id="element-78">
                Y funcionó a medias. O no funcionó. O no sabías si estaba
                funcionando.
              </p>
            </FadeIn>

            <FadeIn data-id="element-79">
              <div className="my-8 text-center" data-id="element-80">
                <h3 className="text-2xl md:text-3xl font-bold text-gold-400 font-serif leading-tight" data-id="element-81">
                  ¿Por qué? Porque un remedio suelto no es un protocolo.
                </h3>
              </div>
            </FadeIn>

            <FadeIn data-id="element-82">
              <p data-id="element-83">
                Es como tener los ingredientes de una receta sin saber las
                cantidades, el orden ni el tiempo de cocción.
              </p>
              <p className="mt-3" data-id="element-84">
                La diferencia entre una infección que se frena en 24 horas y una
                que se convierte en bronquitis no está en el ingrediente que
                usás.
              </p>
              <p className="text-xl text-white font-bold mt-5 text-center bg-navy-800 p-5 rounded-xl border border-navy-700" data-id="element-85">
                Está en <span className="text-emerald-400" data-id="element-86">cuándo</span> actuás,{' '}
                <span className="text-emerald-400" data-id="element-87">cuánto</span> tomás,{' '}
                <span className="text-emerald-400" data-id="element-88">en qué orden</span> lo hacés
                y <span className="text-emerald-400" data-id="element-89">cómo combinás</span> los
                elementos para crear sinergia real.
              </p>
            </FadeIn>

            <FadeIn data-id="element-90">
              <p className="mt-6 text-center" data-id="element-91">
                Ese conocimiento específico es lo que generaciones anteriores
                tenían y que nosotros perdimos.
              </p>
              <p className="text-center" data-id="element-92">
                No porque no existiera. Sino porque nadie se tomó el trabajo de
                organizarlo.
              </p>
              <p className="text-gold-500 font-bold text-2xl mt-4 text-center" data-id="element-93">
                Hasta ahora.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* UNIQUE MECHANISM */}
      <section className="py-8 px-4 bg-navy-800 border-y border-gold-500/10" data-id="element-94">
        <div className="max-w-3xl mx-auto" data-id="element-95">
          <FadeIn data-id="element-96">
            <div className="flex justify-center mb-4" data-id="element-97">
              <Droplets className="w-12 h-12 text-emerald-500" data-id="element-98" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-serif mb-6 text-center" data-id="element-99">
              EL MECANISMO ÚNICO
            </h2>
          </FadeIn>

          <div className="space-y-5 text-lg text-gray-300" data-id="element-100">
            <FadeIn data-id="element-101">
              <p className="font-medium text-white text-xl text-center mb-6" data-id="element-102">
                Hay algo que los laboratorios farmacéuticos no quieren que
                sepas.
              </p>
            </FadeIn>

            <FadeIn data-id="element-103">
              <p data-id="element-104">
                Las bacterias llevan 500 millones de años en guerra contra las
                plantas.
              </p>
              <p className="mt-3" data-id="element-105">
                Y las plantas, durante todo ese tiempo, desarrollaron compuestos
                químicos específicamente diseñados para neutralizar, paralizar y
                eliminar microorganismos patógenos.
              </p>
              <p className="mt-3" data-id="element-106">
                Estos compuestos se llaman{' '}
                <strong className="text-emerald-400" data-id="element-107">
                  fitoconstituyentes.
                </strong>
              </p>
            </FadeIn>

            <FadeIn data-id="element-108">
              <div className="bg-navy-900 p-6 rounded-2xl border-l-4 border-emerald-500 mt-6" data-id="element-109">
                <p className="mb-3" data-id="element-110">
                  Y tienen algo que ningún antibiótico de laboratorio puede
                  igualar:
                </p>
                <p className="text-xl text-white font-bold mb-3" data-id="element-111">
                  Actúan a través de múltiples mecanismos simultáneos.
                </p>
                <p className="text-gray-400" data-id="element-112">
                  Una bacteria necesitaría mutar en 7 puntos diferentes al mismo
                  tiempo para volverse resistente al ajo. La probabilidad
                  matemática de eso es prácticamente imposible.
                </p>
              </div>
            </FadeIn>

            <FadeIn data-id="element-113">
              <div className="text-center mt-8" data-id="element-114">
                <p className="text-gold-400 font-serif text-2xl leading-relaxed" data-id="element-115">
                  Por eso, en 5.000 años de uso documentado del ajo como
                  antibiótico, nunca se registró un caso de bacteria resistente.
                </p>
                <p className="text-white font-bold mt-4 uppercase tracking-wider" data-id="element-116">
                  Eso no es tradición. Eso es ciencia vegetal acumulada durante
                  milenios.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* PRODUCT INTRO */}
      <section className="py-10 px-4 bg-navy-900 relative overflow-hidden" data-id="element-117">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[100px] pointer-events-none" data-id="element-118"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-119">
          <FadeIn data-id="element-120">
            <p className="text-emerald-400 font-bold tracking-widest uppercase text-sm mb-4" data-id="element-121">
              🌿 PRESENTAMOS:
            </p>
            <h2 className="text-5xl md:text-7xl font-bold text-white font-serif mb-4 drop-shadow-lg" data-id="element-122">
              EL BOTIQUÍN ANTIBIÓTICO NATURAL
            </h2>
            <h3 className="text-2xl md:text-3xl text-gold-400 font-serif mb-4" data-id="element-123">
              El Sistema Completo de Salud Familiar Natural en 4 Tomos
            </h3>
            <p className="text-lg text-gray-300 italic max-w-3xl mx-auto mb-8" data-id="element-124">
              El protocolo de 7 días para frenar infecciones, gripes y virus sin
              recurrir a la farmacia. Con dosis exactas, tablas por edad,
              recetas de alta concentración y el sistema para tener todo listo
              antes de necesitarlo.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 4 TOMOS SECTION */}
      <section className="py-8 px-4 bg-navy-800" data-id="element-125">
        <div className="max-w-4xl mx-auto" data-id="element-126">
          <FadeIn data-id="element-127">
            <div className="text-center mb-6" data-id="element-128">
              <BookOpen className="w-12 h-12 text-gold-500 mx-auto mb-3" data-id="element-129" />
              <h2 className="text-3xl md:text-4xl font-bold text-white font-serif mb-3" data-id="element-130">
                LOS 4 TOMOS — QUÉ RECIBÍS
              </h2>
            </div>
          </FadeIn>

          <div className="space-y-6" data-id="element-131">
            {/* Tomo 1 */}
            <FadeIn delay={0.1} data-id="element-132">
              <div className="bg-navy-900 rounded-2xl border border-navy-700 overflow-hidden shadow-xl" data-id="element-133">
                <div className="bg-emerald-900/30 p-6 border-b border-emerald-500/20" data-id="element-134">
                  <h3 className="text-2xl font-bold text-white font-serif" data-id="element-135">
                    <span className="text-emerald-400 mr-2" data-id="element-136">📗 TOMO 1:</span> EL
                    ESCUDO ANTIBIÓTICO
                  </h3>
                  <p className="text-gold-400 mt-2 font-medium" data-id="element-137">
                    Los 7 Pilares de la Medicina Natural que Reemplazaron a los
                    Laboratorios durante Siglos
                  </p>
                </div>
                <div className="p-6 md:p-8" data-id="element-138">
                  <p className="text-gray-300 mb-6" data-id="element-139">
                    Vas a descubrir los 7 antibióticos naturales con mayor
                    evidencia científica publicada:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-8" data-id="element-140">
                    <div className="flex items-start gap-3" data-id="element-141">
                      <span className="text-xl" data-id="element-142">🧄</span>
                      <p className="text-gray-300" data-id="element-143">
                        <strong className="text-white" data-id="element-144">El Ajo</strong> — La
                        penicilina rusa. Activo contra más de 15 bacterias
                        documentadas
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-145">
                      <span className="text-xl" data-id="element-146">🧅</span>
                      <p className="text-gray-300" data-id="element-147">
                        <strong className="text-white" data-id="element-148">
                          La Cebolla Morada
                        </strong>{' '}
                        — 3 veces más quercetina antiviral que la variedad
                        blanca
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-149">
                      <span className="text-xl" data-id="element-150">🫚</span>
                      <p className="text-gray-300" data-id="element-151">
                        <strong className="text-white" data-id="element-152">El Jengibre</strong> — El
                        ibuprofeno natural sin efectos secundarios gástricos
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-153">
                      <span className="text-xl" data-id="element-154">🌿</span>
                      <p className="text-gray-300" data-id="element-155">
                        <strong className="text-white" data-id="element-156">
                          El Aceite de Orégano
                        </strong>{' '}
                        — El carvacrol más potente de los 7 pilares
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-157">
                      <span className="text-xl" data-id="element-158">🐝</span>
                      <p className="text-gray-300" data-id="element-159">
                        <strong className="text-white" data-id="element-160">El Propóleo</strong> — El
                        escudo de la colmena. Perfecto para niños
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-161">
                      <span className="text-xl" data-id="element-162">🌸</span>
                      <p className="text-gray-300" data-id="element-163">
                        <strong className="text-white" data-id="element-164">La Equinácea</strong> —
                        No mata la bacteria. Le enseña a tu cuerpo a eliminarla
                      </p>
                    </div>
                    <div className="flex items-start gap-3 md:col-span-2" data-id="element-165">
                      <span className="text-xl" data-id="element-166">🌿</span>
                      <p className="text-gray-300" data-id="element-167">
                        <strong className="text-white" data-id="element-168">El Tomillo</strong> — El
                        guardián de los pulmones. Específico para sistema
                        respiratorio
                      </p>
                    </div>
                  </div>
                  <div className="bg-navy-800 p-5 rounded-xl border border-navy-700" data-id="element-169">
                    <p className="text-white font-medium mb-3" data-id="element-170">
                      Para cada pilar vas a conocer exactamente:
                    </p>
                    <ul className="space-y-2 text-sm text-gray-400" data-id="element-171">
                      <li className="flex items-center gap-2" data-id="element-172">
                        <Check className="w-4 h-4 text-emerald-500" data-id="element-173" /> El
                        compuesto activo y cómo funciona a nivel celular
                      </li>
                      <li className="flex items-center gap-2" data-id="element-174">
                        <Check className="w-4 h-4 text-emerald-500" data-id="element-175" /> Las
                        bacterias y virus que puede eliminar con estudios de
                        referencia
                      </li>
                      <li className="flex items-center gap-2" data-id="element-176">
                        <Check className="w-4 h-4 text-emerald-500" data-id="element-177" /> El
                        protocolo de uso con dosis por edad y peso
                      </li>
                      <li className="flex items-center gap-2" data-id="element-178">
                        <Check className="w-4 h-4 text-emerald-500" data-id="element-179" /> Cómo
                        conservarlo para máxima potencia
                      </li>
                      <li className="flex items-center gap-2" data-id="element-180">
                        <Check className="w-4 h-4 text-emerald-500" data-id="element-181" /> Las
                        combinaciones de sinergia que multiplican el efecto
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Tomo 2 */}
            <FadeIn delay={0.2} data-id="element-182">
              <div className="bg-navy-900 rounded-2xl border border-navy-700 overflow-hidden shadow-xl" data-id="element-183">
                <div className="bg-blue-900/30 p-6 border-b border-blue-500/20" data-id="element-184">
                  <h3 className="text-2xl font-bold text-white font-serif" data-id="element-185">
                    <span className="text-blue-400 mr-2" data-id="element-186">📘 TOMO 2:</span> EL
                    PROTOCOLO DE 7 DÍAS
                  </h3>
                  <p className="text-gold-400 mt-2 font-medium" data-id="element-187">
                    El Sistema de Frenado Mecánico que Detiene la Infección
                    Antes de que Pase a Mayores
                  </p>
                </div>
                <div className="p-6 md:p-8" data-id="element-188">
                  <p className="text-gray-300 mb-6 italic" data-id="element-189">
                    El libro que querías tener la última vez que alguien en tu
                    familia se enfermó y no sabías exactamente qué hacer.
                  </p>
                  <p className="text-white font-medium mb-4" data-id="element-190">
                    Protocolo completo día por día y hora por hora para:
                  </p>

                  <div className="space-y-4 mb-6" data-id="element-191">
                    <div className="bg-navy-800 p-4 rounded-lg border-l-4 border-red-500" data-id="element-192">
                      <p className="font-bold text-white" data-id="element-193">
                        🔴 Tipo A — Infecciones Respiratorias
                      </p>
                      <p className="text-gray-400 text-sm mt-1" data-id="element-194">
                        Gripe, resfrío, anginas, sinusitis, bronquitis,
                        laringitis
                      </p>
                    </div>
                    <div className="bg-navy-800 p-4 rounded-lg border-l-4 border-blue-500" data-id="element-195">
                      <p className="font-bold text-white" data-id="element-196">
                        🔵 Tipo B — Infecciones Digestivas
                      </p>
                      <p className="text-gray-400 text-sm mt-1" data-id="element-197">
                        Gastroenteritis viral, intoxicación alimentaria,
                        desequilibrio de flora
                      </p>
                    </div>
                    <div className="bg-navy-800 p-4 rounded-lg border-l-4 border-yellow-500" data-id="element-198">
                      <p className="font-bold text-white" data-id="element-199">
                        🟡 Tipo C — Infecciones de Piel
                      </p>
                      <p className="text-gray-400 text-sm mt-1" data-id="element-200">
                        Heridas infectadas, picaduras, furúnculos, infecciones
                        superficiales
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-300 text-sm" data-id="element-201">
                    Con tablas de dosificación exacta para cada grupo de edad y
                    el Registro de Mejoría Diaria para saber en todo momento si
                    el protocolo está funcionando.
                  </p>
                </div>
              </div>
            </FadeIn>

            {/* Tomo 3 */}
            <FadeIn delay={0.3} data-id="element-202">
              <div className="bg-navy-900 rounded-2xl border border-navy-700 overflow-hidden shadow-xl" data-id="element-203">
                <div className="bg-orange-900/30 p-6 border-b border-orange-500/20" data-id="element-204">
                  <h3 className="text-2xl font-bold text-white font-serif" data-id="element-205">
                    <span className="text-orange-400 mr-2" data-id="element-206">📙 TOMO 3:</span>{' '}
                    TRATAMIENTOS DE ESPECIFICIDAD
                  </h3>
                  <p className="text-gold-400 mt-2 font-medium" data-id="element-207">
                    El Bisturí Natural: Protocolos de Alta Precisión
                  </p>
                </div>
                <div className="p-6 md:p-8" data-id="element-208">
                  <p className="text-gray-300 mb-6" data-id="element-209">
                    Cuando el protocolo general no alcanza, necesitás el
                    especialista. Protocolos específicos de alta concentración
                    para:
                  </p>

                  <div className="grid md:grid-cols-3 gap-6 mb-6" data-id="element-210">
                    <div data-id="element-211">
                      <p className="font-bold text-white mb-2 border-b border-navy-700 pb-2" data-id="element-212">
                        Campo Respiratorio:
                      </p>
                      <p className="text-gray-400 text-sm" data-id="element-213">
                        Anginas recurrentes, sinusitis crónica, bronquitis de
                        repetición, laringitis, otitis
                      </p>
                    </div>
                    <div data-id="element-214">
                      <p className="font-bold text-white mb-2 border-b border-navy-700 pb-2" data-id="element-215">
                        Campo Urinario:
                      </p>
                      <p className="text-gray-400 text-sm" data-id="element-216">
                        Cistitis recurrente, apoyo renal, prostatitis natural
                      </p>
                    </div>
                    <div data-id="element-217">
                      <p className="font-bold text-white mb-2 border-b border-navy-700 pb-2" data-id="element-218">
                        Campo Digestivo Profundo:
                      </p>
                      <p className="text-gray-400 text-sm" data-id="element-219">
                        Gastritis por H. pylori, candidiasis sistémica,
                        intestino permeable
                      </p>
                    </div>
                  </div>

                  <div className="bg-gold-500/10 p-4 rounded-lg border border-gold-500/20 text-center" data-id="element-220">
                    <p className="text-gold-400 font-bold" data-id="element-221">
                      Con las 10 Recetas Maestras de potencia terapéutica real.
                    </p>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Tomo 4 */}
            <FadeIn delay={0.4} data-id="element-222">
              <div className="bg-navy-900 rounded-2xl border border-navy-700 overflow-hidden shadow-xl" data-id="element-223">
                <div className="bg-red-900/30 p-6 border-b border-red-500/20" data-id="element-224">
                  <h3 className="text-2xl font-bold text-white font-serif" data-id="element-225">
                    <span className="text-red-400 mr-2" data-id="element-226">📕 TOMO 4:</span>{' '}
                    ANTIBIÓTICOS PREPARADOS
                  </h3>
                  <p className="text-gold-400 mt-2 font-medium" data-id="element-227">
                    Cómo Convertir las Plantas en Medicamentos de Larga Duración
                  </p>
                </div>
                <div className="p-6 md:p-8" data-id="element-228">
                  <p className="text-white font-medium mb-4" data-id="element-229">
                    El nivel de la maestría:
                  </p>
                  <ul className="space-y-3 text-gray-300" data-id="element-230">
                    <li className="flex items-center gap-3" data-id="element-231">
                      <Check className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-232" />{' '}
                      Tinturas madre que duran{' '}
                      <strong className="text-white ml-1" data-id="element-233">2 años</strong>
                    </li>
                    <li className="flex items-center gap-3" data-id="element-234">
                      <Check className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-235" />{' '}
                      Oximieles antibióticos que los niños toman sin resistencia
                    </li>
                    <li className="flex items-center gap-3" data-id="element-236">
                      <Check className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-237" />{' '}
                      Extractos glicerinados sin alcohol (aptos bebés)
                    </li>
                    <li className="flex items-center gap-3" data-id="element-238">
                      <Check className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-239" />{' '}
                      Inhalaciones concentradas de acción directa
                    </li>
                    <li className="flex items-center gap-3" data-id="element-240">
                      <Check className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-241" />{' '}
                      Sistema de almacenamiento y etiquetado completo
                    </li>
                  </ul>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 2 BONOS SECTION */}
      <section className="py-8 px-4 bg-navy-900 border-t border-gold-500/10" data-id="element-242">
        <div className="max-w-4xl mx-auto" data-id="element-243">
          <FadeIn data-id="element-244">
            <div className="text-center mb-6" data-id="element-245">
              <Gift className="w-12 h-12 text-gold-500 mx-auto mb-3" data-id="element-246" />
              <h2 className="text-3xl md:text-4xl font-bold text-white font-serif mb-3" data-id="element-247">
                LOS 2 BONOS EXCLUSIVOS
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-248">
            {/* Bono 1 */}
            <FadeIn delay={0.1} className="h-full" data-id="element-249">
              <div className="bg-navy-800 border-t-4 border-emerald-500 rounded-b-2xl p-6 h-full shadow-lg relative overflow-hidden" data-id="element-250">
                <div className="absolute top-0 right-0 bg-emerald-500 text-navy-900 font-bold text-xs px-3 py-1 rounded-bl-lg" data-id="element-251">
                  HOY GRATIS
                </div>
                <h3 className="text-xl font-bold text-white font-serif mb-2" data-id="element-252">
                  🎁 BONO #1 — SEMÁFORO DE URGENCIAS
                </h3>
                <p className="text-emerald-400 font-medium mb-4" data-id="element-253">
                  Guía de Decisión en 60 Segundos
                </p>
                <p className="text-gray-400 text-sm italic mb-6" data-id="element-254">
                  Valor real: $5.000 ARS
                </p>

                <p className="text-gray-300 mb-6" data-id="element-255">
                  El sistema visual de 3 colores que te permite decidir en menos
                  de 60 segundos:{' '}
                  <strong className="text-white" data-id="element-256">
                    ¿Esto lo manejo en casa o voy a la guardia ahora mismo?
                  </strong>
                </p>

                <ul className="space-y-2 text-sm text-gray-400" data-id="element-257">
                  <li className="flex items-start gap-2" data-id="element-258">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" data-id="element-259" />{' '}
                    Algoritmo de decisión en 60 segundos
                  </li>
                  <li className="flex items-start gap-2" data-id="element-260">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" data-id="element-261" />{' '}
                    <span className="text-red-400 font-medium" data-id="element-262">Tabla Roja</span>{' '}
                    — Derivación inmediata
                  </li>
                  <li className="flex items-start gap-2" data-id="element-263">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" data-id="element-264" />{' '}
                    <span className="text-yellow-400 font-medium" data-id="element-265">
                      Tabla Amarilla
                    </span>{' '}
                    — Vigilancia activa con punto de corte
                  </li>
                  <li className="flex items-start gap-2" data-id="element-266">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" data-id="element-267" />{' '}
                    <span className="text-emerald-400 font-medium" data-id="element-268">
                      Tabla Verde
                    </span>{' '}
                    — Manejo en casa con protocolo
                  </li>
                  <li className="flex items-start gap-2" data-id="element-269">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" data-id="element-270" />{' '}
                    Ficha especial niños + Ficha adultos mayores
                  </li>
                  <li className="flex items-start gap-2" data-id="element-271">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" data-id="element-272" />{' '}
                    Póster imprimible para la heladera
                  </li>
                  <li className="flex items-start gap-2" data-id="element-273">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" data-id="element-274" />{' '}
                    Tarjeta bolsillo para la cartera
                  </li>
                </ul>
              </div>
            </FadeIn>

            {/* Bono 2 */}
            <FadeIn delay={0.2} className="h-full" data-id="element-275">
              <div className="bg-navy-800 border-t-4 border-gold-500 rounded-b-2xl p-6 h-full shadow-lg relative overflow-hidden" data-id="element-276">
                <div className="absolute top-0 right-0 bg-gold-500 text-navy-900 font-bold text-xs px-3 py-1 rounded-bl-lg" data-id="element-277">
                  HOY GRATIS
                </div>
                <h3 className="text-xl font-bold text-white font-serif mb-2" data-id="element-278">
                  🎁 BONO #2 — KIT DE PREPARACIÓN EN 30 MINUTOS
                </h3>
                <p className="text-gold-400 font-medium mb-4" data-id="element-279">
                  Etiquetas, Stock y Lista de Compras
                </p>
                <p className="text-gray-400 text-sm italic mb-6" data-id="element-280">
                  Valor real: $5.000 ARS
                </p>

                <p className="text-gray-300 mb-6" data-id="element-281">
                  Transforma el conocimiento en un botiquín real y organizado en
                  una sola tarde.
                </p>

                <ul className="space-y-2 text-sm text-gray-400" data-id="element-282">
                  <li className="flex items-start gap-2" data-id="element-283">
                    <Check className="w-4 h-4 text-gold-500 shrink-0 mt-0.5" data-id="element-284" />{' '}
                    Cronómetro de 30 minutos paso a paso
                  </li>
                  <li className="flex items-start gap-2" data-id="element-285">
                    <Check className="w-4 h-4 text-gold-500 shrink-0 mt-0.5" data-id="element-286" />{' '}
                    Listas de compras Nivel 1, 2 y 3 con costos estimados
                  </li>
                  <li className="flex items-start gap-2" data-id="element-287">
                    <Check className="w-4 h-4 text-gold-500 shrink-0 mt-0.5" data-id="element-288" />{' '}
                    Inventario del botiquín por zonas
                  </li>
                  <li className="flex items-start gap-2" data-id="element-289">
                    <Check className="w-4 h-4 text-gold-500 shrink-0 mt-0.5" data-id="element-290" />{' '}
                    Guía de almacenamiento completa
                  </li>
                  <li className="flex items-start gap-2" data-id="element-291">
                    <Check className="w-4 h-4 text-gold-500 shrink-0 mt-0.5" data-id="element-292" />{' '}
                    Etiquetas imprimibles para frascos y jarabes
                  </li>
                  <li className="flex items-start gap-2" data-id="element-293">
                    <Check className="w-4 h-4 text-gold-500 shrink-0 mt-0.5" data-id="element-294" />{' '}
                    Tracker mensual de reposición para todo el año
                  </li>
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-8 px-4 bg-navy-800 border-t border-gold-500/10" data-id="element-295">
        <div className="max-w-4xl mx-auto" data-id="element-296">
          <FadeIn data-id="element-297">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white font-serif mb-6" data-id="element-298">
              FAMILIAS QUE YA TIENEN SU BOTIQUÍN
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-5" data-id="element-299">
            {[{
            stars: 5,
            quote: 'La última vez que mi hijo de 5 años tuvo anginas, seguí el Protocolo del Tomo 2 al pie de la letra. En 48 horas estaba jugando de nuevo. Sin antibiótico. Sin guardia. Sin gastarnos $20.000 pesos que no teníamos.',
            name: 'María González',
            role: 'Madre de 3 hijos',
            location: 'Córdoba, Argentina'
          }, {
            stars: 5,
            quote: 'Tengo 3 nietos y siempre fui al médico por cualquier cosa. Ahora tengo el Semáforo en la heladera y sé exactamente cuándo ir y cuándo no. Me cambió completamente la forma de manejar las enfermedades de los chicos.',
            name: 'Susana Rodríguez',
            role: 'Abuela y cuidadora',
            location: 'Buenos Aires'
          }, {
            stars: 5,
            quote: 'Mi marido tenía bronquitis y no queríamos darle más antibióticos. Seguimos el Jarabe Bronquial Maestro del Tomo 3. En 4 días estaba trabajando. Impresionante.',
            name: 'Laura Pérez',
            role: 'Naturista y madre',
            location: 'Rosario, Argentina'
          }, {
            stars: 5,
            quote: 'El Tomo 3 sobre candidiasis y H. pylori es lo más completo que encontré en español. Información de calidad, bien organizada y con bibliografía científica real.',
            name: 'Andrea Martínez',
            role: 'Terapeuta holística',
            location: 'Mendoza, Argentina'
          }].map((t, idx) => <FadeIn key={idx} delay={idx * 0.08} data-id="element-300">
                <div className="bg-navy-900 p-6 rounded-2xl border border-navy-700 h-full flex flex-col shadow-lg" data-id="element-301">
                  <div className="flex text-gold-500 mb-3" data-id="element-302">
                    {[...Array(5)].map((_, i) => <Star key={i} className={`w-5 h-5 ${i < t.stars ? 'fill-gold-500 text-gold-500' : 'fill-navy-600 text-navy-600'}`} data-id="element-303" />)}
                  </div>
                  <p className="text-gray-300 italic leading-relaxed flex-1 text-lg mb-4" data-id="element-304">
                    "{t.quote}"
                  </p>
                  <div className="pt-3 border-t border-navy-700" data-id="element-305">
                    <p className="text-white font-bold" data-id="element-306">{t.name}</p>
                    <p className="text-gray-500 text-sm" data-id="element-307">
                      {t.role} · {t.location}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* UPSELL INTRO */}
      <section className="py-10 px-4 bg-navy-900 border-t border-gold-500/20 relative overflow-hidden" data-id="element-308">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-[120px] pointer-events-none" data-id="element-309"></div>
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-310">
          <FadeIn data-id="element-311">
            <div className="text-center mb-6" data-id="element-312">
              <span className="text-5xl mb-4 block" data-id="element-313">🔥</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white font-serif mb-3" data-id="element-314">
                MOMENTO — ANTES DE CONFIRMAR TU COMPRA
              </h2>
              <p className="text-2xl text-gold-400 font-serif italic" data-id="element-315">
                Completá Tu Sistema en Un Solo Paso
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-316">
            <div className="bg-navy-800/80 p-6 rounded-2xl border-l-4 border-gold-500 mb-8 italic text-lg text-gray-300 shadow-lg" data-id="element-317">
              "El Botiquín Antibiótico te da el poder de curar desde adentro.
              Pero nuestros abuelos nunca curaron solo desde adentro. Siempre
              fue adentro Y afuera. Al mismo tiempo."
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-318">
            <div className="space-y-5 text-lg text-gray-300 mb-10" data-id="element-319">
              <h3 className="text-2xl font-bold text-white font-serif mb-4" data-id="element-320">
                La Mitad del Sistema que Todavía Te Falta
              </h3>
              <p data-id="element-321">
                Ahora que tenés el protocolo para frenar infecciones internas,
                hay algo que las abuelas sabían perfectamente y que la medicina
                moderna separó artificialmente:
              </p>
              <p className="text-xl text-white font-bold my-4 text-center" data-id="element-322">
                La curación más poderosa siempre fue la combinación.
              </p>

              <ul className="space-y-3 bg-navy-800 p-5 rounded-xl border border-navy-700" data-id="element-323">
                <li data-id="element-324">
                  El jarabe de tomillo para los bronquios{' '}
                  <strong className="text-gold-400" data-id="element-325">+</strong> la pomada de
                  eucalipto en el pecho.
                </li>
                <li data-id="element-326">
                  El protocolo antibiótico interno para la infección{' '}
                  <strong className="text-gold-400" data-id="element-327">+</strong> la pomada de
                  caléndula para la piel dañada.
                </li>
                <li data-id="element-328">
                  El té de jengibre y cúrcuma para la inflamación interna{' '}
                  <strong className="text-gold-400" data-id="element-329">+</strong> la pomada de
                  árnica sobre el golpe.
                </li>
              </ul>

              <p className="text-xl text-center font-bold text-white mt-6" data-id="element-330">
                Adentro y afuera. Dos frentes de ataque simultáneos.
              </p>
              <p className="text-center text-emerald-400 font-medium" data-id="element-331">
                Cuando usás ambos sistemas al mismo tiempo, los resultados no se
                suman. Se multiplican.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-332">
            <div className="text-center mb-8" data-id="element-333">
              <p className="text-gold-500 font-bold tracking-widest uppercase text-sm mb-3" data-id="element-334">
                🌿 MEJORÁ TU PACK CON:
              </p>
              <h2 className="text-5xl md:text-6xl font-bold text-white font-serif mb-3 drop-shadow-md" data-id="element-335">
                LAS 50 POMADAS MEDICINALES
              </h2>
              <h3 className="text-xl md:text-2xl text-gold-400 mb-2" data-id="element-336">
                Que Usaban Antes de las Farmacias
              </h3>
              <p className="text-gray-400 italic" data-id="element-337">
                El sistema externo que completa tu botiquín natural
              </p>
            </div>
          </FadeIn>

          {/* Upsell Comparison */}
          <FadeIn delay={0.4} data-id="element-338">
            <div className="mb-10" data-id="element-339">
              <h4 className="text-center text-white font-bold mb-4" data-id="element-340">
                Por Qué Este Es el Complemento Natural Perfecto
              </h4>
              <div className="grid grid-cols-2 gap-px bg-navy-700 border border-navy-700 rounded-xl overflow-hidden" data-id="element-341">
                <div className="bg-navy-800 p-4 md:p-5" data-id="element-342">
                  <p className="font-bold text-emerald-400 mb-3 text-center text-sm md:text-base" data-id="element-343">
                    🌿 El Botiquín Antibiótico
                  </p>
                  <ul className="space-y-2 text-xs md:text-sm text-gray-300" data-id="element-344">
                    <li data-id="element-345">
                      Cura desde <strong className="text-white" data-id="element-346">adentro</strong>
                    </li>
                    <li data-id="element-347">Jarabes, tés, tinturas, extractos</li>
                    <li data-id="element-348">Sistema respiratorio, digestivo, urinario</li>
                    <li data-id="element-349">
                      Acción <strong className="text-white" data-id="element-350">sistémica</strong>
                    </li>
                    <li className="pt-2 border-t border-navy-700 text-emerald-400 font-bold text-center" data-id="element-351">
                      La mitad interna
                    </li>
                  </ul>
                </div>
                <div className="bg-navy-800 p-4 md:p-5" data-id="element-352">
                  <p className="font-bold text-gold-400 mb-3 text-center text-sm md:text-base" data-id="element-353">
                    🌿 Las 50 Pomadas
                  </p>
                  <ul className="space-y-2 text-xs md:text-sm text-gray-300" data-id="element-354">
                    <li data-id="element-355">
                      Cura desde <strong className="text-white" data-id="element-356">afuera</strong>
                    </li>
                    <li data-id="element-357">Pomadas, ungüentos, cremas naturales</li>
                    <li data-id="element-358">Piel, golpes, inflamaciones, picaduras</li>
                    <li data-id="element-359">
                      Acción{' '}
                      <strong className="text-white" data-id="element-360">
                        localizada y directa
                      </strong>
                    </li>
                    <li className="pt-2 border-t border-navy-700 text-gold-400 font-bold text-center" data-id="element-361">
                      La mitad externa
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Upsell Features */}
          <FadeIn delay={0.5} data-id="element-362">
            <div className="bg-navy-800/60 border border-navy-600 rounded-2xl p-6 space-y-3 mb-8" data-id="element-363">
              <h4 className="text-white font-bold mb-3 text-center" data-id="element-364">
                Algunas Pomadas que Vas a Aprender a Preparar
              </h4>
              {[{
              title: 'Pomada de Árnica',
              desc: 'Para golpes y hematomas'
            }, {
              title: 'Pomada de Caléndula',
              desc: 'Para la piel irritada y heridas'
            }, {
              title: 'Pomada de Romero',
              desc: 'Para dolores musculares y articulares'
            }, {
              title: 'Pomada de Lavanda',
              desc: 'Para picaduras e irritaciones'
            }, {
              title: 'Pomada de Eucalipto',
              desc: 'Para congestión (aplicar en pecho)'
            }, {
              title: 'Pomada de Sábila',
              desc: 'Para quemaduras y piel dañada'
            }, {
              title: 'Pomada de Consuelda',
              desc: 'Para articulaciones y tendones'
            }].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-365">
                  <Leaf className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" data-id="element-366" />
                  <p className="text-gray-300" data-id="element-367">
                    <strong className="text-white" data-id="element-368">{item.title}</strong> —{' '}
                    {item.desc}
                  </p>
                </div>)}
              <div className="pt-3 text-center text-gold-400 font-medium italic" data-id="element-369">
                Y 43 recetas más... hasta completar las 50 pomadas naturales
              </div>
            </div>
          </FadeIn>

          {/* Upsell Bonuses */}
          <FadeIn delay={0.6} data-id="element-370">
            <div className="bg-navy-900 border border-gold-500/30 rounded-2xl p-6 mb-8 shadow-lg" data-id="element-371">
              <h4 className="text-center text-xl font-bold text-white font-serif mb-4" data-id="element-372">
                Además Recibís Estos 2 Bonos Extra
              </h4>
              <div className="space-y-4" data-id="element-373">
                <div data-id="element-374">
                  <p className="font-bold text-gold-400 mb-1" data-id="element-375">
                    🎁 Bono Extra 1: 100 Plantas Medicinales y Sus Beneficios
                  </p>
                  <p className="text-gray-400 text-sm" data-id="element-376">
                    Los mismos conocimientos de plantas que usás en el Botiquín,
                    ahora amplificados con 100 variedades completas
                  </p>
                </div>
                <div data-id="element-377">
                  <p className="font-bold text-gold-400 mb-1" data-id="element-378">
                    🎁 Bono Extra 2: Guía de Aceites Medicinales Naturales
                  </p>
                  <p className="text-gray-400 text-sm" data-id="element-379">
                    Los mismos aceites base que usás en el Botiquín son los
                    mismos que usás en las pomadas. Un conocimiento amplifica al
                    otro
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OFFER CARDS */}
      <section className="py-10 px-4 bg-navy-900 relative" data-id="element-380">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/10 via-navy-900 to-navy-900 -z-10" data-id="element-381"></div>
        <div className="max-w-4xl mx-auto" data-id="element-382">
          <FadeIn data-id="element-383">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white font-serif mb-3" data-id="element-384">
              ELEGÍ TU PACK
            </h2>
            <p className="text-center text-gray-400 mb-8" data-id="element-385">
              Seleccioná la opción que mejor se adapte a tu familia.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-386">
            <div className="max-w-md mx-auto bg-[#111827] p-4 md:p-6 rounded-[2rem] border border-navy-600 shadow-2xl" data-id="element-387">
              {/* Pro Card (First) */}
              <div onClick={() => setSelectedOffer('pro')} className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all mb-4 flex justify-between items-start ${selectedOffer === 'pro' ? 'border-emerald-500 bg-navy-800/80 shadow-[0_0_15px_rgba(16,185,129,0.15)]' : 'border-navy-600 bg-navy-800/40 hover:border-navy-500'}`} data-id="element-388">
                <div className="flex items-start gap-4" data-id="element-389">
                  <div className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selectedOffer === 'pro' ? 'border-emerald-500' : 'border-gray-500'}`} data-id="element-390">
                    {selectedOffer === 'pro' && <div className="w-3 h-3 bg-emerald-500 rounded-full" data-id="element-391" />}
                  </div>
                  <div data-id="element-392">
                    <div className="flex items-center gap-2 mb-2" data-id="element-393">
                      <Star className="w-5 h-5 text-gold-500 fill-gold-500" data-id="element-394" />
                      <span className="font-bold text-white text-lg tracking-wide leading-tight" data-id="element-395">
                        PACK SISTEMA COMPLETO
                      </span>
                    </div>
                    <div className="flex flex-col gap-2 mb-3 items-start" data-id="element-396">
                      <span className="bg-emerald-600 text-white text-[10px] sm:text-xs font-bold px-2 py-1 rounded flex items-center gap-1 uppercase tracking-wider" data-id="element-397">
                        <Trophy className="w-3 h-3 fill-white" data-id="element-398" /> RECOMENDADO
                      </span>
                    </div>
                    <ul className="text-gray-400 text-sm space-y-1.5" data-id="element-399">
                      <li data-id="element-400">Botiquín (4 Tomos + 2 Bonos)</li>
                      <li className="text-white font-medium" data-id="element-401">
                        + Las 50 Pomadas
                      </li>
                      <li className="text-gray-500 text-xs pl-2" data-id="element-402">
                        - 100 Plantas Medicinales
                      </li>
                      <li className="text-gray-500 text-xs pl-2" data-id="element-403">
                        - Guía de Aceites
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="text-right shrink-0" data-id="element-404">
                  <p className="text-3xl font-bold text-emerald-500 mt-8" data-id="element-405">
                    $19.980
                  </p>
                </div>
              </div>

              {/* Base Card (Second) */}
              <div onClick={() => setSelectedOffer('base')} className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all mb-6 flex justify-between items-center ${selectedOffer === 'base' ? 'border-emerald-500 bg-navy-800/80 shadow-[0_0_15px_rgba(16,185,129,0.15)]' : 'border-navy-600 bg-navy-800/40 hover:border-navy-500'}`} data-id="element-406">
                <div className="flex items-start gap-4" data-id="element-407">
                  <div className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selectedOffer === 'base' ? 'border-emerald-500' : 'border-gray-500'}`} data-id="element-408">
                    {selectedOffer === 'base' && <div className="w-3 h-3 bg-emerald-500 rounded-full" data-id="element-409" />}
                  </div>
                  <div data-id="element-410">
                    <div className="flex items-center gap-2 mb-1" data-id="element-411">
                      <Leaf className="w-5 h-5 text-emerald-500" data-id="element-412" />
                      <span className="font-bold text-white text-lg tracking-wide" data-id="element-413">
                        PACK BASE
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm" data-id="element-414">
                      Solo el Botiquín (4 Tomos + 2 Bonos)
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0" data-id="element-415">
                  <p className="text-2xl font-bold text-white" data-id="element-416">$12.990</p>
                </div>
              </div>

              {/* CTA Button */}
              <a href={selectedOffer === 'base' ? 'https://fegion.shop/cart/46193580441739:1' : 'https://fegion.shop/cart/46193584799883:1'} target="_blank" rel="noopener noreferrer" className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-bold text-lg py-4 px-6 rounded-xl shadow-[0_0_20px_rgba(16,185,129,0.4)] transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 uppercase tracking-wide text-center leading-tight" data-id="element-417">
                {selectedOffer === 'base' ? 'Elegir Solo el Pack Base' : 'SÍ, QUIERO EL SISTEMA COMPLETO AHORA'}
              </a>
              <p className="text-center text-gray-500 text-xs mt-3" data-id="element-418">
                Acceso inmediato a TODO — Pago 100% seguro
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-419"></FadeIn>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-8 px-4 bg-navy-800 border-t border-gold-500/10" data-id="element-420">
        <div className="max-w-3xl mx-auto" data-id="element-421">
          <FadeIn data-id="element-422">
            <h2 className="text-3xl font-bold text-center text-white font-serif mb-5" data-id="element-423">
              PREGUNTAS FRECUENTES
            </h2>
          </FadeIn>

          <div className="space-y-3" data-id="element-424">
            {[{
            q: '¿Esto reemplaza al médico?',
            a: 'No. El Semáforo de Urgencias (Bono #1) te enseña exactamente cuándo ir al médico y cuándo no. Para las infecciones comunes del hogar (el 80% de los casos), el protocolo natural es efectivo. Para urgencias reales, el sistema te deriva claramente.'
          }, {
            q: '¿Funciona para niños?',
            a: 'Sí. El Tomo 2 incluye tablas de dosificación específicas para niños de 2-6 años, 6-12 años y adolescentes, con las plantas seguras para cada grupo etario.'
          }, {
            q: '¿Dónde consigo los ingredientes?',
            a: 'En el Bono #2 (Kit de Preparación) encontrás la lista completa con dónde conseguir cada elemento. La mayoría del Nivel 1 se consigue en cualquier verdulería o súper.'
          }, {
            q: '¿Necesito conocimientos previos?',
            a: 'No. Todo está explicado desde cero con preparaciones paso a paso y tablas de referencia rápida.'
          }, {
            q: '¿Cuándo recibo el material?',
            a: 'Inmediatamente después del pago. Recibís un email con acceso a todos los PDFs para descargar al instante.'
          }, {
            q: '¿Y si no me sirve?',
            a: 'Tenés 7 días de garantía total. Sin preguntas, sin formularios, reembolso completo si es que no cumple con lo que te decimos.'
          }].map((faq, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-425">
                <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden cursor-pointer transition-colors hover:border-navy-600" onClick={() => toggleFaq(idx)} data-id="element-426">
                  <div className="p-4 flex justify-between items-center" data-id="element-427">
                    <h4 className="text-white font-bold pr-4 flex items-start gap-3" data-id="element-428">
                      <span className="text-gold-500" data-id="element-429">❓</span> {faq.q}
                    </h4>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} data-id="element-430" />
                  </div>
                  <div className={`px-4 pb-4 text-gray-300 pl-12 transition-all duration-300 ease-in-out ${openFaq === idx ? 'block opacity-100' : 'hidden opacity-0'}`} data-id="element-431">
                    {faq.a}
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* WHO IS THIS FOR */}
      <section className="py-8 px-4 bg-navy-800 border-t border-gold-500/10" data-id="element-432">
        <div className="max-w-3xl mx-auto" data-id="element-433">
          <FadeIn data-id="element-434">
            <h2 className="text-3xl font-bold text-center text-white font-serif mb-5" data-id="element-435">
              PARA QUIÉN ES ESTE SISTEMA
            </h2>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-436">
            <div className="bg-navy-900 p-6 rounded-2xl border border-navy-700 shadow-lg space-y-3" data-id="element-437">
              {['Para vos si tenés hijos y querés dejar de correr a la guardia cada vez que alguien en la familia tose', 'Para vos si cuidás a un adulto mayor y necesitás saber cuándo actuar y cuándo derivar', 'Para vos si ya usás plantas medicinales pero sin protocolo organizado', 'Para vos si los antibióticos le caen mal a tu familia y buscás una alternativa real', 'Para vos si querés recuperar el conocimiento que nuestros abuelos tenían', 'Para vos si querés ahorrar en consultas sin poner en riesgo la salud de nadie'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-438">
                  <Check className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" data-id="element-439" />
                  <p className="text-gray-300 text-lg" data-id="element-440">{item}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* VALUE STACK TABLE */}
      <section className="py-8 px-4 bg-navy-900" data-id="element-441">
        <div className="max-w-3xl mx-auto" data-id="element-442">
          <FadeIn data-id="element-443">
            <h2 className="text-3xl font-bold text-center text-white font-serif mb-5" data-id="element-444">
              EL STACK COMPLETO DE VALOR
            </h2>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-445">
            <div className="rounded-2xl border border-gold-500/30 shadow-xl overflow-hidden" data-id="element-446">
              <table className="w-full text-left border-collapse" data-id="element-447">
                <thead data-id="element-448">
                  <tr className="bg-navy-800 border-b border-gold-500/30" data-id="element-449">
                    <th className="p-4 md:p-5 text-white font-bold text-sm md:text-lg w-2/3" data-id="element-450">
                      Producto
                    </th>
                    <th className="p-4 md:p-5 text-gray-400 font-medium text-right text-sm md:text-lg w-1/3" data-id="element-451">
                      Valor Real
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-700 bg-navy-900 text-sm md:text-base" data-id="element-452">
                  {[{
                  label: '📗 Tomo 1: El Escudo Antibiótico',
                  value: '$15.000 ARS'
                }, {
                  label: '📘 Tomo 2: El Protocolo de 7 Días',
                  value: '$15.000 ARS'
                }, {
                  label: '📙 Tomo 3: Tratamientos de Especificidad',
                  value: '$15.000 ARS'
                }, {
                  label: '📕 Tomo 4: Antibióticos Preparados',
                  value: '$15.000 ARS'
                }, {
                  label: '🎁 Bono 1: Semáforo de Urgencias',
                  value: '$5.000 ARS'
                }, {
                  label: '🎁 Bono 2: Kit de Preparación en 30 Min',
                  value: '$5.000 ARS'
                }].map((row, idx) => <tr key={idx} className="hover:bg-navy-800/50 transition-colors" data-id="element-453">
                      <td className="p-3 md:p-4 text-gray-300" data-id="element-454">{row.label}</td>
                      <td className="p-3 md:p-4 text-gray-500 text-right line-through" data-id="element-455">
                        {row.value}
                      </td>
                    </tr>)}
                  <tr className="bg-navy-800 border-t-2 border-navy-600" data-id="element-456">
                    <td className="p-4 md:p-5 text-white font-bold text-lg md:text-xl" data-id="element-457">
                      VALOR TOTAL
                    </td>
                    <td className="p-4 md:p-5 text-gray-400 font-bold text-right text-lg md:text-xl line-through" data-id="element-458">
                      $70.000 ARS
                    </td>
                  </tr>
                  <tr className="bg-emerald-900/20 border-t-2 border-emerald-500/50" data-id="element-459">
                    <td className="p-4 md:p-5 text-emerald-400 font-black text-xl md:text-2xl" data-id="element-460">
                      HOY PAGÁS SOLO
                    </td>
                    <td className="p-4 md:p-5 text-emerald-400 font-black text-right text-xl md:text-2xl" data-id="element-461">
                      $12.990 ARS
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* POMADAS VALUE BOX */}
      <section className="py-8 px-4 bg-navy-800 border-t border-gold-500/10" data-id="element-462">
        <div className="max-w-3xl mx-auto" data-id="element-463">
          <FadeIn data-id="element-464">
            <div className="text-center bg-navy-900 p-6 rounded-2xl border border-emerald-500/30" data-id="element-465">
              <h4 className="text-white font-bold text-xl mb-4" data-id="element-466">
                Lo Que Recibís Si Agregás Las Pomadas Ahora
              </h4>
              <ul className="space-y-2 text-gray-300 mb-4 text-left max-w-sm mx-auto" data-id="element-467">
                <li className="flex justify-between" data-id="element-468">
                  <span data-id="element-469">🌿 Las 50 Pomadas</span>{' '}
                  <span className="text-gray-500 line-through" data-id="element-470">$15.000</span>
                </li>
                <li className="flex justify-between" data-id="element-471">
                  <span data-id="element-472">🎁 100 Plantas</span>{' '}
                  <span className="text-gray-500 line-through" data-id="element-473">$10.000</span>
                </li>
                <li className="flex justify-between" data-id="element-474">
                  <span data-id="element-475">🎁 Guía de Aceites</span>{' '}
                  <span className="text-gray-500 line-through" data-id="element-476">$8.000</span>
                </li>
                <li className="flex justify-between pt-2 border-t border-navy-600 font-bold text-white" data-id="element-477">
                  <span data-id="element-478">VALOR TOTAL</span>{' '}
                  <span className="text-gray-500 line-through" data-id="element-479">$33.000</span>
                </li>
                <li className="flex justify-between text-gray-400" data-id="element-480">
                  <span data-id="element-481">Precio regular</span> <span data-id="element-482">$9.990</span>
                </li>
              </ul>
              <div className="bg-emerald-900/30 p-3 rounded-xl border border-emerald-500/50" data-id="element-483">
                <p className="text-emerald-400 font-bold text-xl mb-1" data-id="element-484">
                  ⭐ HOY — Solo por agregar al Botiquín
                </p>
                <p className="text-white font-black text-3xl" data-id="element-485">$6.990 ARS más</p>
              </div>
              <p className="text-gray-400 text-sm mt-3 italic" data-id="element-486">
                El complemento externo que hace que tu sistema interno funcione
                el doble de bien, por la mitad del precio regular.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* DUAL CTA SECTION */}
      <section className="py-10 px-4 bg-navy-900 relative border-t border-gold-500/10" data-id="element-487">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/10 via-navy-900 to-navy-900 -z-10" data-id="element-488"></div>
        <div className="max-w-2xl mx-auto text-center" data-id="element-489">
          <FadeIn data-id="element-490">
            <p className="text-emerald-500 font-bold tracking-widest uppercase text-sm mb-6" data-id="element-491">
              👇 ELEGÍ TU PACK Y ACCEDÉ AHORA
            </p>

            {/* Option A — Combo Pro */}
            <div className="bg-navy-800 border-2 border-emerald-500 rounded-2xl p-6 md:p-8 mb-5 shadow-[0_0_30px_rgba(16,185,129,0.1)]" data-id="element-492">
              <p className="text-emerald-400 font-bold uppercase tracking-wider text-sm mb-2" data-id="element-493">
                ⭐ PACK SISTEMA COMPLETO — RECOMENDADO
              </p>
              <h3 className="text-2xl font-bold text-white font-serif mb-2" data-id="element-494">
                Botiquín Antibiótico Natural + Las 50 Pomadas Medicinales
              </h3>
              <p className="text-gray-400 text-sm italic mb-4" data-id="element-495">
                6 libros digitales + 4 Bonos + Garantía 7 días
              </p>

              <div className="flex items-center justify-center mb-4" data-id="element-496">
                <span className="text-4xl font-black text-white font-serif" data-id="element-497">
                  $19.980{' '}
                  <span className="text-xl text-gray-400 font-sans font-normal" data-id="element-498">
                    ARS
                  </span>
                </span>
              </div>
              <a href="https://fegion.shop/cart/46193584799883:1" target="_blank" rel="noopener noreferrer" className="w-full block bg-[#10B981] hover:bg-[#059669] text-white font-bold text-lg py-5 px-8 rounded-xl shadow-[0_0_25px_rgba(16,185,129,0.4)] transition-all transform hover:scale-[1.02] uppercase tracking-wide mb-3 text-center" data-id="element-499">
                🌿 SÍ, QUIERO EL SISTEMA COMPLETO
              </a>
              <p className="text-gray-400 text-sm" data-id="element-500">
                Acceso inmediato — Descarga instantánea — Pago 100% seguro
              </p>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 my-6" data-id="element-501">
              <div className="flex-1 h-px bg-navy-700" data-id="element-502"></div>
              <span className="text-gray-500 font-medium" data-id="element-503">— o —</span>
              <div className="flex-1 h-px bg-navy-700" data-id="element-504"></div>
            </div>

            {/* Option B — Pack Base */}
            <div className="mb-6" data-id="element-505">
              <p className="text-gray-400 uppercase tracking-wider text-sm mb-2" data-id="element-506">
                PACK BASE
              </p>
              <p className="text-white font-bold mb-1" data-id="element-507">
                Solo el Botiquín Antibiótico Natural
              </p>
              <p className="text-gray-500 text-xs italic mb-3" data-id="element-508">
                4 Tomos + 2 Bonos + Garantía 7 días
              </p>
              <p className="text-2xl text-white font-bold mb-3" data-id="element-509">$12.990 ARS</p>
              <a href="https://fegion.shop/cart/46193580441739:1" target="_blank" rel="noopener noreferrer" className="w-full block bg-navy-700 hover:bg-navy-600 border border-navy-500 text-white font-bold text-base py-4 px-8 rounded-xl transition-all transform hover:scale-[1.02] mb-2 text-center" data-id="element-510">
                Elegir Solo el Pack Base
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-8 px-4 bg-navy-900" data-id="element-511">
        <div className="max-w-2xl mx-auto" data-id="element-512">
          <FadeIn data-id="element-513">
            <div className="bg-navy-800 border border-gold-500/30 rounded-2xl p-6 md:p-8 text-center shadow-xl relative overflow-hidden" data-id="element-514">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-gold-500 to-transparent" data-id="element-515"></div>
              <ShieldCheck className="w-14 h-14 text-gold-500 mx-auto mb-4" data-id="element-516" />
              <h2 className="text-2xl md:text-3xl font-bold text-white font-serif mb-4" data-id="element-517">
                🛡️ Garantía de Satisfacción Total — 7 Días
              </h2>
              <p className="text-gray-300 text-lg mb-3" data-id="element-518">
                Accedé al sistema completo. Leé los 4 Tomos. Armá tu botiquín.
                Usá el Semáforo.
              </p>
              <p className="text-gray-300 text-lg mb-4" data-id="element-519">
                Si por cualquier razón el contenido no cumple tus expectativas,
                tenés{' '}
                <strong className="text-white" data-id="element-520">
                  7 días para solicitar el reembolso completo.
                </strong>
              </p>
              <p className="text-gray-400 italic mb-4" data-id="element-521">
                Sin preguntas. Sin formularios. Sin vueltas.
              </p>
              <p className="text-gold-400 font-bold text-xl uppercase tracking-wide" data-id="element-522">
                El riesgo es 100% nuestro.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* URGENCY */}

      {/* CIERRE FINAL */}
      <section className="py-10 px-4 bg-navy-900 border-t border-navy-800" data-id="element-523">
        <div className="max-w-3xl mx-auto text-center" data-id="element-524">
          <FadeIn data-id="element-525">
            <div className="bg-navy-800 p-6 rounded-2xl border border-emerald-500/30 mb-6" data-id="element-526">
              <p className="text-xl md:text-2xl text-white font-serif font-bold leading-relaxed" data-id="element-527">
                El conocimiento de que la naturaleza proveyó, mucho antes de los
                laboratorios, un sistema completo de defensa para el cuerpo
                humano.
              </p>
              <p className="text-gold-400 font-bold mt-3 text-xl" data-id="element-528">
                Adentro y afuera. Juntos.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER & LEGAL */}
      <footer className="py-8 px-4 bg-navy-950 border-t border-navy-800 text-center" data-id="element-529">
        <div className="max-w-4xl mx-auto" data-id="element-530">
          <div className="bg-navy-900 border border-navy-700 p-5 rounded-xl mb-6 text-left" data-id="element-531">
            <p className="text-gray-400 text-sm leading-relaxed" data-id="element-532">
              <strong className="text-gray-300" data-id="element-533">⚠️ AVISO LEGAL:</strong> Los
              productos descritos tienen carácter educativo e informativo. No
              reemplazan el diagnóstico ni el tratamiento de un profesional de
              la salud certificado. Ante síntomas graves o señales de alarma,
              buscar atención médica inmediata. El uso de plantas medicinales
              puede interactuar con medicamentos recetados.
            </p>
          </div>

          <Leaf className="w-6 h-6 text-emerald-500/50 mx-auto mb-2" data-id="element-534" />
          <p className="text-emerald-500/70 font-serif italic text-sm mb-1" data-id="element-535">
            🌿 El Botiquín Antibiótico Natural + Las 50 Pomadas Medicinales
          </p>
          <p className="text-gray-600 text-xs" data-id="element-536">by Fegion</p>
        </div>
      </footer>
    </div>;
}